import React, {useId, useRef, useState} from 'react';
import AdvancedWordProcessor from '../../components/editors/AdvancedWordProcessor';
import CustomSpreadsheet from '../../components/editors/CustomSpreadsheet';
import StaticSpreadsheetViewer from '../../components/editors/StaticSpreadsheetViewer';
import {flushEditorChanges} from '../../components/editors/useEditorFrame';

// Legacy IQ answers were plain text, not HTML. Preserve their literal contents.
export function practiceWrittenAnswer(answer) {
    if (typeof answer === 'string') return {type:'descriptive',view:'word',wordContent:answer.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/\n/g,'<br>')};
    return answer && typeof answer === 'object' ? answer : {type:'descriptive',view:'word',wordContent:''};
}

export default function PracticeAnswerWorkspace({answer,onChange,disabled=false,expanded=false,onExpand}) {
    const value = practiceWrittenAnswer(answer), latest = useRef(value);
    latest.current = value;
    const view = value.view === 'excel' ? 'excel' : 'word';
    const [visited,setVisited] = useState({[view]:true});
    const id = useId();
    // Merge against the latest value: Word and Spreadsheet can flush together
    // before a click, and neither update may erase the other answer part.
    const update = (part,content) => {
        if (disabled) return;
        const next = {...latest.current,type:'descriptive',[part]:content};
        latest.current = next; onChange(next);
    };
    const changeView = next => {
        flushEditorChanges();
        setVisited(previous=>({...previous,[next]:true}));
        const updated = {...latest.current,type:'descriptive',view:next};
        latest.current = updated; onChange(updated);
    };
    const navigateTab = event => {
        if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 'word' : event.key === 'End' ? 'excel' : view === 'word' ? 'excel' : 'word';
        changeView(next); document.getElementById(`${id}-${next}-tab`)?.focus();
    };
    return <div className="iq-response-workspace">
        <div className="iq-response-toolbar">
            <div role="tablist" aria-label="Answer workspace" onKeyDown={navigateTab}>
                <button type="button" id={`${id}-word-tab`} role="tab" aria-selected={view==='word'} aria-controls={`${id}-word-panel`} tabIndex={view==='word'?0:-1} onClick={()=>changeView('word')}>Word</button>
                <button type="button" id={`${id}-excel-tab`} role="tab" aria-selected={view==='excel'} aria-controls={`${id}-excel-panel`} tabIndex={view==='excel'?0:-1} onClick={()=>changeView('excel')}>Spreadsheet</button>
            </div>
            {onExpand && <button type="button" className="iq-response-expand" aria-pressed={expanded} onClick={onExpand}>{expanded?'Split view':'Widen workspace'}</button>}
        </div>
        <p className="iq-response-hint">{disabled?'Your saved response · Switch tabs to review both parts.':'Write in Word, calculate in Spreadsheet, or use both. Both parts autosave to your account, including your formulas and drawings. Wait for “Saved to your account” before closing.'}</p>
        <div role="tabpanel" id={`${id}-word-panel`} aria-labelledby={`${id}-word-tab`} hidden={view!=='word'} className="iq-response-editor">
            {(visited.word || view==='word') && <AdvancedWordProcessor value={value.wordContent || ''} onChange={content=>update('wordContent',content)} readOnly={disabled} preserveViewport/>}
        </div>
        <div role="tabpanel" id={`${id}-excel-panel`} aria-labelledby={`${id}-excel-tab`} hidden={view!=='excel'} className={`iq-response-editor${disabled?' iq-response-readonly':''}`}>
            {(visited.excel || view==='excel') && (disabled ? <StaticSpreadsheetViewer data={value.excelData}/> : <CustomSpreadsheet autosaveDraft data={value.excelData} onChange={data=>update('excelData',data)} preserveViewport/>)}
        </div>
    </div>;
}
